'use client';

import React, { useRef, useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import AppLogo from '@/components/ui/AppLogo';

const wislyFeatures = [
    {
        title: 'Student Progress Dashboard',
        desc: 'Real-time visibility into academic performance, attendance, and learning milestones — for students and parents alike.',
        icon:
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 14l4-5 3 3 4-6 3 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                <rect x="1" y="1" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5" />
            </svg>

    },
    {
        title: 'Tutor–Parent Communication',
        desc: 'Structured, in-platform messaging that keeps parents informed and tutors accountable without the chaos of external apps.',
        icon:
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 5h14v9a2 2 0 01-2 2H5a2 2 0 01-2-2V5z" stroke="currentColor" strokeWidth="1.5" />
                <path d="M3 5l7 6 7-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>

    },
    {
        title: 'Institutional Analytics',
        desc: 'Aggregate insights for schools and learning institutions — track cohort performance, identify gaps, and act on data.',
        icon:
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2v16M2 10h16M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>

    },
    {
        title: 'Adaptive Learning Pathways',
        desc: 'AI-powered curriculum paths that adjust to how each student learns — not a one-size-fits-all approach.',
        icon:
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M2 10c0-4.418 3.582-8 8-8s8 3.582 8 8-3.582 8-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M10 6v4l3 3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>

    },
    {
        title: 'Session Scheduling',
        desc: 'Integrated calendar and booking system for tutoring sessions — with reminders, rescheduling, and attendance tracking.',
        icon:
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect x="2" y="3" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
                <path d="M14 1v4M6 1v4M2 8h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>

    },
    {
        title: 'Multi-role Access',
        desc: 'One platform, four stakeholders — students, parents, tutors, and institutions each get a purpose-built experience.',
        icon:
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="7" cy="7" r="3" stroke="currentColor" strokeWidth="1.5" />
                <circle cx="14" cy="7" r="2" stroke="currentColor" strokeWidth="1.5" />
                <path d="M1 17c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M14 12c2 0 4 1 4 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>

    }];


export default function WislyDetail() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setVisible(true); },
            { threshold: 0.1 }
        );
        if (sectionRef?.current) observer?.observe(sectionRef?.current);
        return () => observer?.disconnect();
    }, []);

    return (
        <section id="wisly" ref={sectionRef} className="py-24 px-6 md:px-10 border-b border-border opacity-100">
            <div className="max-w-7xl mx-auto">
                {/* Product header */}
                <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-3 h-3 rounded-full bg-[var(--wisly)]" />
                            <span className="section-label text-[var(--wisly)]">Education Technology</span>
                        </div>
                        <div className="flex items-center gap-3">

                            <AppLogo src='/assets/images/wisly/app_logo.png' size={100} />

                            <h2 className="text-hero-lg text-foreground mb-5">Wisly</h2>
                        </div>
                        <p className="text-xl text-muted-foreground leading-relaxed mb-6">
                            Reimagining education through a connected ecosystem for students, parents, tutors, and institutions.
                        </p>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-8">
                            Education is broken not because of lack of content — but because the people involved in a student's journey aren't connected. Wisly fixes that. One intelligent platform that brings every stakeholder together, with tools built specifically for how they work.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link href="https://wisly.codigo.com.ng" target='_blank' className="btn-primary" style={{ background: 'var(--wisly)', boxShadow: '0 0 24px rgba(124,58,237,0.3)' }}>
                                Explore Wisly
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Link>
                            <Link href="/contact" className="btn-secondary">
                                Schedule a demo
                            </Link>
                        </div>
                    </div>

                    {/* Product image */}
                    <div className={`relative rounded-2xl overflow-hidden h-80 glass-card ${visible ? 'animate-fade-up' : 'opacity-0'}`}>
                        <AppImage
                            src="https://img.rocket.new/generatedImages/rocket_gen_img_16c7a96c8-1772107701443.png"
                            alt="Open books and a laptop on a bright wooden desk, warm daylight, clean educational workspace"
                            fill
                            className="object-cover" />

                        <div className="absolute inset-0 bg-gradient-to-t from-card/80 via-card/20 to-transparent" />
                        {/* Floating stat */}
                        <div className="absolute bottom-6 left-6 glass-card px-5 py-4 rounded-xl">
                            <p className="stat-number text-[var(--wisly)]" style={{ fontSize: '2rem' }}>4</p>
                            <p className="text-xs text-muted-foreground mt-0.5">Stakeholder roles</p>
                        </div>
                    </div>
                </div>

                {/* Features — asymmetric bento */}
                <div>
                    <p className="section-label text-muted-foreground mb-8">Platform capabilities</p>

                    {/* BENTO GRID AUDIT:
                    Array has 6 cards: [Student Progress, Tutor-Parent Comm, Institutional Analytics, Adaptive Learning, Session Scheduling, Multi-role Access]
                    Row 1 (auto-rows): [col-1: StudentProgress cs-2] [col-3: TutorParent cs-1] [col-4: InstitutionalAnalytics cs-1]
                    Row 2 (auto-rows): [col-1: AdaptiveLearning cs-1] [col-2: SessionScheduling cs-1] [col-3: MultiRole cs-2]
                    Placed 6/6 cards ✓
                    */}
                    <div className="grid md:grid-cols-4 gap-4">
                        {/* Card 1 — wide */}
                        <div className={`md:col-span-2 glass-card p-7 rounded-2xl group hover:border-primary/30 transition-all duration-300 card-hover-lift ${visible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '0ms' }}>
                            <div className="w-10 h-10 rounded-xl bg-[var(--wisly)]/10 text-[var(--wisly)] flex items-center justify-center mb-5 group-hover:bg-[var(--wisly)]/20 transition-colors">
                                {wislyFeatures?.[0]?.icon}
                            </div>
                            <h4 className="text-base font-700 text-foreground mb-2 tracking-tight">{wislyFeatures?.[0]?.title}</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">{wislyFeatures?.[0]?.desc}</p>
                        </div>

                        {/* Card 2 */}
                        <div className={`glass-card p-7 rounded-2xl group hover:border-primary/30 transition-all duration-300 card-hover-lift ${visible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '80ms' }}>
                            <div className="w-10 h-10 rounded-xl bg-[var(--wisly)]/10 text-[var(--wisly)] flex items-center justify-center mb-5 group-hover:bg-[var(--wisly)]/20 transition-colors">
                                {wislyFeatures?.[1]?.icon}
                            </div>
                            <h4 className="text-base font-700 text-foreground mb-2 tracking-tight">{wislyFeatures?.[1]?.title}</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">{wislyFeatures?.[1]?.desc}</p>
                        </div>

                        {/* Card 3 */}
                        <div className={`glass-card p-7 rounded-2xl group hover:border-primary/30 transition-all duration-300 card-hover-lift ${visible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '160ms' }}>
                            <div className="w-10 h-10 rounded-xl bg-[var(--wisly)]/10 text-[var(--wisly)] flex items-center justify-center mb-5 group-hover:bg-[var(--wisly)]/20 transition-colors">
                                {wislyFeatures?.[2]?.icon}
                            </div>
                            <h4 className="text-base font-700 text-foreground mb-2 tracking-tight">{wislyFeatures?.[2]?.title}</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">{wislyFeatures?.[2]?.desc}</p>
                        </div>

                        {/* Card 4 */}
                        <div className={`glass-card p-7 rounded-2xl group hover:border-primary/30 transition-all duration-300 card-hover-lift ${visible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '240ms' }}>
                            <div className="w-10 h-10 rounded-xl bg-[var(--wisly)]/10 text-[var(--wisly)] flex items-center justify-center mb-5 group-hover:bg-[var(--wisly)]/20 transition-colors">
                                {wislyFeatures?.[3]?.icon}
                            </div>
                            <h4 className="text-base font-700 text-foreground mb-2 tracking-tight">{wislyFeatures?.[3]?.title}</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">{wislyFeatures?.[3]?.desc}</p>
                        </div>

                        {/* Card 5 */}
                        <div className={`glass-card p-7 rounded-2xl group hover:border-primary/30 transition-all duration-300 card-hover-lift ${visible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '320ms' }}>
                            <div className="w-10 h-10 rounded-xl bg-[var(--wisly)]/10 text-[var(--wisly)] flex items-center justify-center mb-5 group-hover:bg-[var(--wisly)]/20 transition-colors">
                                {wislyFeatures?.[4]?.icon}
                            </div>
                            <h4 className="text-base font-700 text-foreground mb-2 tracking-tight">{wislyFeatures?.[4]?.title}</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">{wislyFeatures?.[4]?.desc}</p>
                        </div>

                        {/* Card 6 — wide */}
                        <div className={`md:col-span-2 glass-card p-7 rounded-2xl group hover:border-primary/30 transition-all duration-300 card-hover-lift ${visible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '400ms' }}>
                            <div className="w-10 h-10 rounded-xl bg-[var(--wisly)]/10 text-[var(--wisly)] flex items-center justify-center mb-5 group-hover:bg-[var(--wisly)]/20 transition-colors">
                                {wislyFeatures?.[5]?.icon}
                            </div>
                            <h4 className="text-base font-700 text-foreground mb-2 tracking-tight">{wislyFeatures?.[5]?.title}</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">{wislyFeatures?.[5]?.desc}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>);

}